import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from './shared';

const RuleOfThreeCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [c, setC] = useState('');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const aVal = parseFloat(parseBrazilianNumber(a)) || 0;
    const bVal = parseFloat(parseBrazilianNumber(b)) || 0;
    const cVal = parseFloat(parseBrazilianNumber(c)) || 0;

    if (aVal === 0) return;

    const x = (bVal * cVal) / aVal;

    setResult({ x, aVal, bVal, cVal });

    onResultChange([
      { label: 'Valor A', value: aVal.toString() },
      { label: 'Valor B', value: bVal.toString() },
      { label: 'Valor C', value: cVal.toString() },
      { label: 'Resultado (X)', value: x.toLocaleString('pt-BR', { maximumFractionDigits: 4 }) }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Calculadora de Regra de Três</h2>
        <p className="text-theme-muted mb-4">
          Resolva proporções e cálculos de forma simples e direta.
          Ferramenta essencial para matemática e cálculos do dia a dia.
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            label="Valor A"
            placeholder="Ex: 10"
            value={a}
            onChange={setA}
            helpText="Primeira base"
            required
            isNumeric
          />

          <FormField
            label="Valor B"
            placeholder="Ex: 20"
            value={b}
            onChange={setB}
            helpText="Equivalente"
            required
            isNumeric
          />

          <FormField
            label="Valor C"
            placeholder="Ex: 30"
            value={c}
            onChange={setC}
            helpText="Nova base"
            required
            isNumeric
          />
        </div>
        <div className="mt-8 flex justify-center">
          <button
            onClick={calculate}
            className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Proporção
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ResultCard
              title="Resultado (X)"
              value={result.x.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
              description="Valor desconhecido"
              color="blue"
            />

            <ResultCard
              title="Fórmula"
              value="(B × C) ÷ A"
              description="Regra de três"
              color="purple"
            />

            <ResultCard
              title="Cálculo"
              value={`(${result.bVal} × ${result.cVal}) ÷ ${result.aVal}`}
              description="Operação"
              color="emerald"
            />

            <ResultCard
              title="Proporção"
              value="A : B = C : X"
              description="Relação matemática"
              color="amber"
            />
          </div>

          <div className="bg-theme-result rounded-2xl border border-theme-border overflow-hidden">
            <div className="px-6 py-4 bg-theme-input border-b border-theme-border">
              <h3 className="text-lg font-bold text-theme-text">Interpretação</h3>
            </div>
            <div className="p-6">
              <div className="bg-category-work p-4 rounded-2xl border border-theme-border">
                <p className="text-sm font-medium text-theme-text">
                  A está para B, assim como C está para X
                </p>
                <p className="text-xs text-theme-muted mt-2">
                  Se {result.aVal} unidades correspondem a {result.bVal} unidades, então {result.cVal} unidades correspondem a {result.x.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} unidades.
                </p>
              </div>
            </div>
          </div>

          <EducationalBlock
            title="Dicas de Regra de Três"
            tips={[
              "Use para calcular proporções em receitas, escalas e mapas",
              "Mantenha a mesma unidade de medida para valores comparáveis",
              "Verifique se o resultado faz sentido no contexto do problema",
              "Organize os dados em tabela para facilitar visualização"
            ]}
            warnings={[
              "Não se esqueça de converter unidades quando necessário",
              "Cuidado com zeros no denominador (valor A não pode ser zero)",
              "Verifique se a proporção é direta ou inversa",
              "Sempre teste com valores conhecidos para validar o cálculo"
            ]}
          />
        </>
      )}

      {!result && (
        <div className="h-full flex items-center justify-center bg-theme-muted-section rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-theme-input rounded-full flex items-center justify-center text-2xl mb-3">📐</div>
          <p className="font-bold text-base">Preencha os dados e clique em Calcular</p>
        </div>
      )}
    </div>
  );
};

export default RuleOfThreeCalculator;
